/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-nested-ternary */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsCumulative,
  selectTrendTimeWeeklyView,
  selectTrendHotelSegmentTabularVisuals,
  selectTrendHotelSegmentWeeklyTabularVisuals,
  selectTrendHotelSegmentCumulativeTabularVisuals,
  selectTrendHotelFocusOn,
  selectActiveTrendTab,
  selectTrendHotelSegmentTabularFilterList,
  selectRevenueIsDisplayTS,
} from 'modules/dashboard/components/tab-container/trend-tabs/selectors';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import { selectEmbedToken, selectTabList } from 'modules/dashboard/selectors';
import VisualGrid from 'modules/dashboard/components/tab-container/visual-grid';
import { trendActions } from 'modules/dashboard/components/tab-container/trend-tabs/slice';
import VisualCard from 'modules/dashboard/components/tab-container/visual-card';
import ExportToCSV from 'modules/dashboard/components/tab-container/export-visual-data/export-csv';
import { REPORT_TYPE } from 'modules/dashboard/constants';
import { isEmpty } from '../../../../../../../../common/helpers/object';
/**
 * Trend Hotel tabular visual report with filters
 */
const TrendHotelSegmentTabularWidget = ({ setIsSegmentLoaded }) => {
  const dispatch = useDispatch();
  // States are obtained from redux store. Refer each selector function for details.
  const tabularFilterList = useSelector(selectTrendHotelSegmentTabularFilterList);
  const weeklyViewToggle = useSelector(selectTrendTimeWeeklyView);
  const isCumulative = useSelector(selectIsCumulative);
  const tokenDetails = useSelector(selectEmbedToken);
  const visuals = useSelector(selectTrendHotelSegmentTabularVisuals);
  const weeklyVisuals = useSelector(selectTrendHotelSegmentWeeklyTabularVisuals);
  const cumulativeVisuals = useSelector(selectTrendHotelSegmentCumulativeTabularVisuals);
  const revenueIsDisplay = useSelector(selectRevenueIsDisplayTS);
  const hotelFocusOn = useSelector(selectTrendHotelFocusOn);
  const activeTab = useSelector(selectActiveTrendTab);
  const TAB_VALUE = useSelector(selectTabList);

  const [tabularCombined, setTabularCombined] = useState({});
  const [isTabularCombinedRendered, setIsTabularCombinedRendered] = useState({});
  // To get visual details
  const getVisual = (index) => {
    let found;
    if (isCumulative) {
      found = cumulativeVisuals?.find((visual) => visual?.order === index);
    } else if (weeklyViewToggle) {
      found = weeklyVisuals?.find((visual) => visual?.order === index);
    } else {
      found = visuals?.find((visual) => visual?.order === index);
    }
    return found;
  };
  // To get visuals based on the configuration
  const getWidget = (
    index,
    cssClassName,
    setPBIReport = () => {},
    setIsLoaded = () => {},
    setIsRendered = () => {},
    displayFunction = () => {},
    filter = tabularFilterList
  ) => {
    const found = getVisual(index);
    return (
      found && (
        <VisualCard
          cssClassName={cssClassName}
          accessToken={tokenDetails?.accessToken}
          embedUrl={found?.embedUrl}
          id={found?.reportId}
          visualName={found?.name}
          pageName={found?.pageName}
          pageDisplayName={found?.pageDisplayName}
          reportName={found?.reportName}
          filters={filter}
          setPBIReport={(r) => setPBIReport(r)}
          setIsLoaded={(r) => setIsLoaded(r)}
          setIsRendered={(d) => setIsRendered(d)}
          tab={TAB_VALUE.PBI_TREND}
          dispatch={dispatch}
          displayFunction={displayFunction}
        />
      )
    );
  };
  // To get visual names based on visual order
  const getVisualName = (order) => getVisual(order)?.name;
  //
  return (
    !isEmpty(tabularFilterList) && (
      <VisualGrid
        isDisplay={revenueIsDisplay}
        visualName={getVisualName(0)}
        visual={getWidget(
          0,
          styles.tabularVisual,
          setTabularCombined,
          setIsSegmentLoaded,
          setIsTabularCombinedRendered,
          trendActions.setRevenueIsDisplayTS
        )}
        onClick={() => {
          dispatch(trendActions.setRevenueIsDisplayTS(!revenueIsDisplay));
        }}
        toggleFragment={
          <ExportToCSV
            exportExcel
            report={tabularCombined}
            isLoaded={isTabularCombinedRendered && revenueIsDisplay}
            fileName={
              isCumulative ? 'TXH_Cumulative' : weeklyViewToggle ? 'TXH_Weekly' : 'TXH_Daily'
            }
          />
        }
        isWorkbookHotelComparison={activeTab === REPORT_TYPE.TREND_SEGMENT}
        comparisonHotel={hotelFocusOn}
        displayHotelName
      />
    )
  );
};
//
export default TrendHotelSegmentTabularWidget;
